import { useState } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';

import styles from './TemporaryPasswordDialog.module.css';

export interface TemporaryPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Whose password this is, e.g. "Rahul Mehta". */
  personName: string;
  /** The login they sign in with (email or mobile). */
  login: string | null;
  /** The one-time password, or null when the admin typed their own. */
  password: string | null;
}

/**
 * Shows a newly created or reset password exactly once. The API never returns it again, and the
 * person must replace it at their first sign-in.
 */
export function TemporaryPasswordDialog({
  open,
  onClose,
  title,
  personName,
  login,
  password,
}: TemporaryPasswordDialogProps) {
  const [copied, setCopied] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className={styles.content}>
        {login && (
          <div className={styles.row}>
            <span className={styles.label}>Signs in with</span>
            <span className={styles.value}>{login}</span>
          </div>
        )}
        {password ? (
          <>
            <div className={styles.row}>
              <span className={styles.label}>One-time password</span>
              <div className={styles.passwordRow}>
                <code className={styles.password}>{password}</code>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={copied ? 'check' : 'copy'}
                  onClick={() => {
                    void navigator.clipboard.writeText(password).then(() => {
                      setCopied(true);
                    });
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            <Alert tone="warning">
              Give this to {personName} now — it is shown only once. They will choose their own
              password when they first sign in.
            </Alert>
          </>
        ) : (
          <Alert tone="info">
            The password you typed is set. {personName} will choose their own password when they
            first sign in.
          </Alert>
        )}
      </div>
    </Dialog>
  );
}
